import { ModuloDTO } from "@/core/domain/dto/privado/aplicacao/modulo/modulo.dto";
import { FuncionalidadeDTO } from "@/core/domain/dto/privado/aplicacao/modulo/funcionalidade.dto";
import { MenuItem } from "primeng/api";

export type BuildMenuItemsOptions = {
    /** Ordena módulos e funcionalidades pelo campo index (usuários normais via token). */
    sortByIndex?: boolean;
    /** Preserva a ordem da API usando a árvore achatada (OWNER / DEVELOPER / SUPORT). */
    orderReference?: Array<ModuloDTO>;
};

function sortByIndex<T extends { index?: number }>(items: Array<T>): Array<T> {
    return [...items].sort((left, right) => (left.index ?? 0) - (right.index ?? 0));
}

function orderModulosByReference(
    modulos: Array<ModuloDTO>,
    reference: Array<ModuloDTO>,
): Array<ModuloDTO> {
    const orderMap = new Map(reference.map((modulo, position) => [modulo.id, position]));
    return [...modulos].sort(
        (left, right) =>
            (orderMap.get(left.id) ?? Number.MAX_SAFE_INTEGER)
            - (orderMap.get(right.id) ?? Number.MAX_SAFE_INTEGER),
    );
}

function resolveModuloOrder(
    modulos: Array<ModuloDTO>,
    options?: BuildMenuItemsOptions,
): Array<ModuloDTO> {
    if (options?.sortByIndex) {
        return sortByIndex(modulos);
    }
    if (options?.orderReference?.length) {
        return orderModulosByReference(modulos, options.orderReference);
    }
    return modulos;
}

function mapFuncionalidadeToMenuItem(funcionalidade: FuncionalidadeDTO): MenuItem {
    return {
        label: funcionalidade.nome,
        icon: funcionalidade.icone,
        routerLink: funcionalidade.url ? [funcionalidade.url] : undefined,
    };
}

function buildModuloSubtree(
    moduloId: number,
    allModulos: Array<ModuloDTO>,
    options?: BuildMenuItemsOptions,
): Array<MenuItem> {
    const childModulos = resolveModuloOrder(
        allModulos.filter((modulo) => modulo.moduloPaiId === moduloId),
        options,
    );
    const currentModulo = allModulos.find((modulo) => modulo.id === moduloId);
    const items: Array<MenuItem> = [];

    for (const childModulo of childModulos) {
        const subtreeItems = buildModuloSubtree(childModulo.id, allModulos, options);
        if (subtreeItems.length > 0) {
            items.push({
                label: childModulo.nome,
                icon: childModulo.icone,
                items: subtreeItems,
            });
        }
    }

    const funcionalidades = options?.sortByIndex
        ? sortByIndex(currentModulo?.funcionalidades ?? [])
        : (currentModulo?.funcionalidades ?? []);

    const funcionalidadeItems = funcionalidades
        .map((funcionalidade) => mapFuncionalidadeToMenuItem(funcionalidade));

    return [...items, ...funcionalidadeItems];
}

export function buildMenuItemsFromModulos(
    allModulos: Array<ModuloDTO>,
    options?: BuildMenuItemsOptions,
): Array<MenuItem> {
    if (!allModulos?.length) {
        return [];
    }

    const rootModulos = resolveModuloOrder(
        allModulos.filter((modulo) => !modulo.moduloPaiId),
        options,
    );

    return rootModulos
        .map((rootModulo) => {
            const items = buildModuloSubtree(rootModulo.id, allModulos, options);
            if (!items.length) {
                return null;
            }
            return {
                label: rootModulo.nome,
                icon: rootModulo.icone,
                items,
            } as MenuItem;
        })
        .filter((item): item is MenuItem => item != null);
}
